import React, { useEffect, useState } from "react";
import { Typography } from "@mui/material";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import { RuleInfo } from "components/rules-management/RuleInfo";
import { useGetRules } from "hooks/rules/useGetRules";
import useDumbPageTitle from "hooks/useDumbPageTitle";
import { useTrackPageView } from "hooks/useTrackPageView";
import { styled } from "@mui/material/styles";
import { FixedHeightPaper } from "components/AlertsManagement/FixedHeightPaper";
import { CreateRuleDialog } from "components/rules-management/RuleDialog/CreateRuleDialog";
import { EditRuleDialog } from "components/rules-management/RuleDialog/EditRuleDialog";
import { useGetTags } from "hooks/tags/useGetTags";
import { Link } from "react-router-dom";
import { Breadcrumbs } from "components/Breadcrumbs";
import { NewRuleTable } from "components/rules-management/NewRuleTable";

const PAGE_TITLE = "Rule Management";

export function RuleManagementPage() {
  const [selectedRuleId, setSelectedRuleId] = useState();
  const [editRule, setEditRule] = useState();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editRuleDialogOpen, setEditRuleDialogOpen] = useState(false);

  const [pageSize, setPageSize] = useState(50);
  const [pageIndex, setPageIndex] = useState(0);

  const { loading, error, rules, count, refetchRules } = useGetRules(
    pageIndex + 1,
    pageSize
  );
  const { loading: loadingTags, tags } = useGetTags();

  useDumbPageTitle(
    <Breadcrumbs>
      <Link to={`/pages/alerts`}>Alerts</Link>
      <div>{PAGE_TITLE}</div>
    </Breadcrumbs>
  );
  useTrackPageView(PAGE_TITLE);

  useEffect(() => {
    if (!loading && !error && !selectedRuleId) {
      setSelectedRuleId(
        rules && rules.length > 0 ? rules[0]?.rule_id : undefined
      );
    }
  }, [loading, error, selectedRuleId, rules]);

  const handleSuccess = ruleId => {
    setSelectedRuleId(ruleId);
    refetchRules();
    setCreateDialogOpen(false);
  };

  const handleOnClickEdit = (e, rule) => {
    e.stopPropagation();
    setEditRule(rule);
    setEditRuleDialogOpen(true);
  };

  const handleEditSuccess = rule => {
    setEditRule(rule);
    setEditRuleDialogOpen(false);
    refetchRules();
  };

  return (
    <>
      <Grid
        container
        direction="row-reverse"
        justifyContent="space-between"
        alignItems="top"
        spacing={2}
      >
        <Grid item xs={12} sm={12} md={12} lg={7}>
          <FixedHeightPaper>
            <RuleInfo ruleId={selectedRuleId} />
          </FixedHeightPaper>
        </Grid>
        <Grid item xs={12} sm={12} md={12} lg={5}>
          <FixedHeightPaper>
            <Header>
              <Typography variant="h4">Rules</Typography>
              <div>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => setCreateDialogOpen(true)}
                >
                  Create new rule
                </Button>
              </div>
            </Header>
            <NewRuleTable
              loading={loading}
              error={error}
              rules={rules}
              selectedRuleId={selectedRuleId}
              count={count ?? 0}
              page={pageIndex}
              pageSize={pageSize}
              onPageChange={setPageIndex}
              onChangePageSize={setPageSize}
              onChangeSelectedRuleId={setSelectedRuleId}
              onClickEdit={handleOnClickEdit}
            />
          </FixedHeightPaper>
        </Grid>
      </Grid>
      <CreateRuleDialog
        open={createDialogOpen}
        loadingTags={loadingTags}
        tags={tags}
        onSuccess={handleSuccess}
        onClose={() => {
          setCreateDialogOpen(false);
        }}
      />
      <EditRuleDialog
        rule={editRule}
        open={editRuleDialogOpen}
        loadingTags={loadingTags}
        tags={tags}
        onSuccess={handleEditSuccess}
        onClose={() => {
          setEditRuleDialogOpen(false);
        }}
      />
    </>
  );
}

const Header = styled("div")`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;
